/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useState, useEffect, useCallback } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';

const useGetBorrowerWebUser = (email: string) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [id, setId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { token } = useAuthContext();

  const { reset } = useAbortController();

  const webUserInfoUrl = process.env.REACT_APP_WEB_USER_INFO;

  const getWebUserInfo = useCallback(async () => {
    if (!token || !email) {
      return;
    }

    const config = reset();

    const formData = {
      email: email,
    };

    const headers = {
      'Session-Token': token,
      'Content-Type': 'application/json',
    };

    setLoading(true);
    setErrorMessage('');
    try {
      const response = await axiosCall({ url: webUserInfoUrl, method: 'post', data: formData }, config, headers);
      if (response.success) {
        const responseFirstName = response.webUser.borrowerFirstName;
        const responseLastName = response.webUser.borrowerLastName;
        const responseWebId = response.webUser.webUserId;
        if (responseFirstName) {
          setFirstName(responseFirstName);
        }
        if (responseLastName) {
          setLastName(response.webUser.borrowerLastName);
        }
        if (responseWebId) {
          setId(responseWebId);
        }
      } else {
        console.error(response.error);
        setErrorMessage(response.error);
      }
    } catch (error) {
      console.error('Unable to retrieve Borrower information: ' + error);
      setErrorMessage('Unable to retrieve Borrower Information.');
    }
    setLoading(false);
  }, [email, reset, token, webUserInfoUrl]);

  useEffect(() => {
    getWebUserInfo();
  }, [getWebUserInfo]);

  return {
    firstName: firstName,
    lastName: lastName,
    webUserId: id,
    webUserInfoErrorMessage: errorMessage,
    webUserInfoLoading: loading,
  };
};

export default useGetBorrowerWebUser;
